import { type Extensions } from "@tiptap/core"
import Document from "@tiptap/extension-document"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import Subscript from "@tiptap/extension-subscript"
import Superscript from "@tiptap/extension-superscript"
import TextAlign from "@tiptap/extension-text-align"
import Typography from "@tiptap/extension-typography"
import Underline from "@tiptap/extension-underline"
import StarterKit from "@tiptap/starter-kit"
import React, { useEffect, useState } from "react"
import QuoteForm from "./quote_form"
import { type BlockProps } from "."
import { graphql, getFragment } from "@/api"
import StaticTiptapDisplay from "@/client/components/static_tiptap_display"
import Input from "@/common/components/input"
import { CheckmarkLarge } from "@/skylticons"

const HeadingDocument = Document.extend({ content: "heading" })
const ParagraphDocument = Document.extend({ content: "paragraph+" })

export const quoteCardHeadingStack: Extensions = [
  StarterKit.configure({
    document: false,
    bold: false,
    italic: false,
    heading: {
      levels: [2],
      HTMLAttributes: {
        class: "mb-4 heading-three",
      },
    },
  }),
  HeadingDocument,
  Placeholder,
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["heading"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const quoteCardParagraphStack: Extensions = [
  StarterKit.configure({
    document: false,
    paragraph: {
      HTMLAttributes: {
        class: "mb-6 lg:mb-4 paragraph-sm",
      },
    },
  }),
  ParagraphDocument,
  Placeholder,
  Underline,
  Link.configure({ openOnClick: false, HTMLAttributes: { target: "_self", rel: "noopener" } }),
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["paragraph"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const QuoteCardFragment = graphql(`
  fragment FrontpagesQuoteCard on FrontpagesQuoteCard {
    id
    headingJSON: heading
    description
    buttonLabel
    inputLabel
    imageAlt
    usps

    image {
      data
      width
      height
      pngDesktop
      pngDesktop2x
      pngTablet
      pngTablet2x
      pngMobile
      pngMobile2x
      webpDesktop
      webpDesktop2x
      webpTablet
      webpTablet2x
      webpMobile
      webpMobile2x
    }

    quoteForm {
      ...FrontpagesQuoteForm
    }
  }
`)

const QuoteCardBlock: React.FC<BlockProps<typeof QuoteCardFragment>> = ({ blockable, index }) => {
  const block = getFragment(QuoteCardFragment, blockable)
  const [formOpen, setFormOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [enabled, setEnabled] = useState(false)

  useEffect(() => {
    setEnabled(true)
  }, [])

  const openForm = (e: React.FormEvent) => {
    e.preventDefault()

    setFormOpen(true)
  }

  return (
    <div className="pb-8 md:container md:mx-auto md:px-3 lg:px-6">
      <div className="flex min-h-[384px] flex-col gap-8 bg-blue-200 px-3 py-8 md:rounded-3xl md:px-10 md:py-12 lg:flex-row lg:gap-16">
        <div className="flex flex-1 flex-col justify-center">
          <StaticTiptapDisplay extensions={quoteCardHeadingStack} json={block.headingJSON} />
          <StaticTiptapDisplay extensions={quoteCardParagraphStack} json={block.description} />

          {block.usps.map((usp, index) => (
            <div key={index} className="paragraph-sm mb-4 flex items-center gap-1">
              <CheckmarkLarge className="w-6 text-info" />
              <span>{usp}</span>
            </div>
          ))}
        </div>
        <div className="flex-1">
          <div className="relative">
            <picture>
              <source
                srcSet={`${block.image.webpDesktop}, ${block.image.webpDesktop2x} 2x`}
                media="(min-width: 1280px)"
                type="image/webp"
              />
              <source
                srcSet={`${block.image.pngDesktop}, ${block.image.pngDesktop2x} 2x`}
                media="(min-width: 1280px)"
              />
              <source
                srcSet={`${block.image.webpTablet}, ${block.image.webpTablet2x} 2x`}
                media="(min-width: 640px)"
                type="image/webp"
              />
              <source srcSet={`${block.image.pngTablet}, ${block.image.pngTablet2x} 2x`} media="(min-width: 640px)" />
              <source srcSet={`${block.image.webpMobile}, ${block.image.webpMobile2x} 2x`} type="image/webp" />
              <img
                srcSet={`${block.image.pngMobile}, ${block.image.pngMobile2x} 2x`}
                width={block.image.width}
                height={block.image.height}
                alt={block.imageAlt || ""}
                className="mb-20 max-h-full w-full overflow-hidden rounded-lg lg:mb-10"
                loading="lazy"
              />
            </picture>
            <div className="absolute left-0 right-0 top-full -mt-6 rounded-xl bg-white px-4 py-6 shadow-[0px_2px_4px_rgba(20,33,61,0.05),0px_8px_16px_rgba(20,33,61,0.15)] lg:right-8 lg:-ml-8 lg:-mt-16 lg:rounded-2xl lg:px-6 lg:py-8">
              <form onSubmit={openForm}>
                <Input
                  name="email"
                  label={`*${block.inputLabel}`}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={!enabled}
                />
                <button
                  className="button button-primary mb-0 w-full lg:w-auto"
                  type="submit"
                  disabled={!enabled}
                  data-gtm={`fp-quote-card-${index}`}
                >
                  {block.buttonLabel}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <QuoteForm form={block.quoteForm} open={formOpen} setOpen={setFormOpen} email={email} />
    </div>
  )
}

export default QuoteCardBlock
